import React, { useCallback, useEffect, useState } from "react";
import { Box, TextField, Button, TablePagination } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import moment from "moment-timezone";

import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import Loader from "../../components/Loader";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import { tokens } from "../../theme";
import { mono, th, td, pill, TablePaginationActions } from "../../components/tableKit";

const fmtDate = (d) => (d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD") : "—");

const EMPTY_FILTERS = { dateFrom: "", dateTo: "", pageName: "", source: "" };

export default function Analytics() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  // What's typed, vs. what's actually been searched against the API/DB.
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (applied.dateFrom) params.append("dateFrom", applied.dateFrom);
    if (applied.dateTo) params.append("dateTo", applied.dateTo);
    if (applied.pageName) params.append("pageName", applied.pageName);
    if (applied.source) params.append("source", applied.source);
    params.append("page", page);
    params.append("size", size);
    const response = await getAllData(`${endPoints.api.ANALYTICS_LIST}?${params.toString()}`);
    if (response?.status === "success") {
      const data = response.data?.rows || [];
      setRows(data);
      setTotal(response.data?.total ?? data.length);
    }
  }, [applied, page, size]);

  useEffect(() => {
    setIsLoading(true);
    load()
      .catch((e) => console.error(e))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const onDraftChange = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const runSearch = () => {
    setPage(0);
    setApplied(draft);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") runSearch();
  };

  if (isLoading && rows.length === 0) return <Loader />;

  return (
    <Box>
      <PageHeader
        title="Analytics"
        crumb="Monitor / Analytics"
        subtitle="Every page view on the public site, not only the ones that end in a signup"
      />

      <SectionCard>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", alignItems: "flex-end", mb: 2 }}>
          <TextField size="small" type="date" label="From" value={draft.dateFrom} sx={{ flex: "1 1 160px" }}
            InputLabelProps={{ shrink: true }} onChange={onDraftChange("dateFrom")} onKeyDown={onKeyDown} />
          <TextField size="small" type="date" label="To" value={draft.dateTo} sx={{ flex: "1 1 160px" }}
            InputLabelProps={{ shrink: true }} onChange={onDraftChange("dateTo")} onKeyDown={onKeyDown} />
          <TextField size="small" label="Page" placeholder="e.g. /home" value={draft.pageName} sx={{ flex: "2 1 220px" }}
            onChange={onDraftChange("pageName")} onKeyDown={onKeyDown} />
          <TextField size="small" label="Source" placeholder="e.g. google" value={draft.source} sx={{ flex: "2 1 220px" }}
            onChange={onDraftChange("source")} onKeyDown={onKeyDown} />
          <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={runSearch}
            sx={{ textTransform: "none", height: 40 }}>
            Search
          </Button>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Page</th>
                <th style={th}>Source</th>
                <th style={th}>Medium</th>
                <th style={th}>Campaign</th>
                <th style={th}>Content</th>
                <th style={th}>Visitor</th>
                <th style={th}>User</th>
                <th style={th}>Country</th>
                <th style={th}>Device</th>
                <th style={th}>IP</th>
                <th style={th}>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td style={{ ...td, color: tokens.textDim }} colSpan={11}>No page views match this filter.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, whiteSpace: "normal", maxWidth: 220 }}>{r.pageName || "—"}</td>
                  <td style={td}>{r.source || "—"}</td>
                  <td style={td}>{r.medium || "—"}</td>
                  <td style={td}>{r.campaign || "—"}</td>
                  <td style={{ ...td, color: tokens.textDim }}>{r.content || "—"}</td>
                  <td style={td}>{pill(r.attributionType === "REGISTERED" ? "Registered" : "Visit",
                    r.attributionType === "REGISTERED" ? "ok" : "neutral")}</td>
                  <td style={{ ...td, fontFamily: mono }}>{r.idUser || "—"}</td>
                  <td style={td}>{r.country || "—"}</td>
                  <td style={td}>
                    <div>{r.browser || "—"}</div>
                    {(r.device || r.operatingSystem) && (
                      <div style={{ fontSize: 11, color: tokens.textDim2 }}>
                        {[r.device, r.operatingSystem].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: mono, fontSize: 11.5, color: tokens.textDim }}>{r.ipAddress || "—"}</td>
                  <td style={{ ...td, fontFamily: mono, fontSize: 11.5, color: tokens.textDim }}>{fmtDate(r.insertionTime)}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={size}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
          ActionsComponent={TablePaginationActions}
        />
      </SectionCard>
    </Box>
  );
}
